import { useCallback } from 'react'
import { asClientInfoStatus } from '@common/interfaces/client'
import { BROADCAST_MENTION_OPTIONS, MentionPurpose, MentionType } from '@common/interfaces/mentions'
import type { IMentionLoader, IMentionSuggestion } from '../components/Common/Mentions'
import {
  MENTION_TYPE_LABELS,
  buildAtlasAISuggestionData,
} from '../components/Common/Mentions/mentionHelpers'

// Cap on how many merged rows the `/` popup renders at once (it's scrollable),
// and the per-list page size we request from the server. The search is pushed
// down to the server (SQL `ILIKE` for prompts, `.includes` for the workflow
// registry) so filtering is authoritative — the client no longer holds the
// whole catalog and slices it, which meant prompts past the first page (and
// prompts colliding with a similarly-named workflow) never surfaced.
const MAX_SLASH_SUGGESTIONS = 50

/**
 * Answers this session has already had from the server, keyed by purpose and
 * search text.
 *
 * Module scope rather than component state: an editor remounts on every send, so
 * a cache that died with it would be empty exactly when someone is typing. What
 * it holds is only ever what the server last said, so serving from it cannot put
 * a partial list on screen — which is the reason nothing is assembled locally.
 */
const searchCache = new Map<string, IMentionSuggestion[]>()

const cacheKey = (purpose: string, search: string) => `${purpose}|${search.toLowerCase()}`

/** Dropped on sign-out, so one viewer never answers for the next. */
export const clearMentionSearchCache = (): void => searchCache.clear()

// Exported so a composer can type the two dispatched actions it forwards here
// against the same shapes, rather than restating them and drifting.
export interface ISlashPrompt {
  id: string
  key: string
}

export interface ISlashWorkflow {
  workflowName: string
}

/**
 * The mention-search action, as every surface takes it. Declared once here
 * because the hook is what consumes it — the components only pass it through.
 */
export type ListMentionSuggestions = (params: {
  name?: string
  purpose?: MentionPurpose
}) => Promise<{ data: IMentionSuggestion[] }>

interface IUseMentionSuggestionsProps {
  listMentionSuggestions?: ListMentionSuggestions
  // 'context' (default) for Arc — mentions feed the AI; 'tag' for AIM and Atlas
  // Tasks, where mentions are navigable chips and Teams can be tagged. See
  // resolveMentionSuggestions.
  purpose?: MentionPurpose
  listPrompts?: (params?: object) => Promise<{ data: { data: ISlashPrompt[] } }>
  listWorkflows?: (params?: object) => Promise<{ data: { data: ISlashWorkflow[] } }>
  /**
   * Offer `@here`/`@channel` as reserved, selectable suggestions alongside people.
   *
   * Opt-in and client-only — these tokens resolve to a *group* of channel
   * recipients at send time (see channels/resolveBroadcastMentions), which only
   * makes sense for channel messaging. `resolveMentionSuggestions` stays generic across every
   * surface (Notes, Tasks, Arc), so this is layered on here rather than added to
   * the shared server query, or Notes/Tasks would suggest a token they can't
   * resolve.
   */
  includeBroadcastMentions?: boolean
}

interface IUseMentionSuggestionsResult {
  /**
   * The server-backed loader, carrying the half it can answer from the session
   * cache without a round trip — see IMentionLoader.
   */
  loadSuggestions: IMentionLoader
  loadSlashSuggestions: (searchText: string) => Promise<IMentionSuggestion[]>
}

const useMentionSuggestions = ({
  listMentionSuggestions,
  purpose = 'context',
  listPrompts,
  listWorkflows,
  includeBroadcastMentions = false,
}: IUseMentionSuggestionsProps): IUseMentionSuggestionsResult => {
  // Matched and listed ahead of the server results, the way Slack lists
  // "here"/"channel" above people — they're what a bare `@` is most often typed
  // for. No round trip: both tokens are fixed and always in scope, so filtering
  // against a two-item list costs nothing a debounced network call would.
  const broadcastFor = useCallback(
    (trimmedSearch: string): IMentionSuggestion[] =>
      includeBroadcastMentions
        ? BROADCAST_MENTION_OPTIONS.filter((option) => option.name.startsWith(trimmedSearch)).map(
            (option, index): IMentionSuggestion => ({
              id: option.id,
              name: option.name,
              type: option.type,
              subtitle: option.description,
              // A sentence, not a type tag — rendered as quiet inline text rather
              // than the uppercase pill the slash menu uses for 'Workflow'.
              subtitleVariant: 'hint',
              avatar: buildAtlasAISuggestionData({ id: option.id, type: option.type }).avatar,
              link: null,
              typeHeader: index === 0 ? 'Notify' : undefined,
            }),
          )
        : [],
    [includeBroadcastMentions],
  )

  /**
   * A search this session has already run, answered without the network.
   *
   * Only ever a cache hit. Painting it on the keystroke is safe precisely because
   * it is what the server last said rather than something assembled here — so it
   * cannot leave a partial list on screen that grows when a reply lands. A miss
   * returns nothing and the caller leaves the current list alone.
   */
  const getLocalSuggestions = useCallback(
    (searchText: string): IMentionSuggestion[] => {
      const trimmedSearch = searchText.trim().toLowerCase()
      const cached = searchCache.get(cacheKey(purpose, trimmedSearch))
      return cached ? [...broadcastFor(trimmedSearch), ...cached] : []
    },
    [broadcastFor, purpose],
  )

  const loadSuggestions: IMentionLoader = useCallback(
    async (searchText: string): Promise<IMentionSuggestion[]> => {
      if (!listMentionSuggestions) return []
      const trimmedSearch = searchText.trim().toLowerCase()
      const broadcastSuggestions = broadcastFor(trimmedSearch)

      const cached = searchCache.get(cacheKey(purpose, trimmedSearch))
      if (cached) return [...broadcastSuggestions, ...cached]

      /**
       * A failure is deliberately left to propagate.
       *
       * Returning the broadcast tokens instead — `[]` on every surface that does
       * not offer them — resolves the promise, so the popup treats it as a
       * successful empty answer and blanks the list. That reads as "no such name
       * exists", which is the thing the shared failure rule exists to prevent; a
       * rejection is what makes it keep the last good list (see useAsyncSearch).
       */
      const response = await listMentionSuggestions({ name: searchText, purpose })
      // Chip tagging ('link'/'tag') groups the dropdown by type: the server
      // returns rows already ordered by type, so tag the first row of each
      // type group with a section header (People, Teams, Clients, …).
      const isGrouped = purpose === 'link' || purpose === 'tag'
      let prevType: MentionType | undefined
      const mapped = response.data.map((suggestion: IMentionSuggestion) => {
        const { link, avatar } = buildAtlasAISuggestionData({
          id: suggestion.id,
          type: suggestion.type,
          clientStatus: asClientInfoStatus(suggestion.clientStatus),
          recordDate: suggestion.recordDate,
          reportType: suggestion.reportType,
        })
        const isNewGroup = isGrouped && !!suggestion.type && suggestion.type !== prevType
        prevType = suggestion.type
        return {
          id: suggestion.id,
          name: suggestion.name,
          avatar,
          link,
          type: suggestion.type,
          clientStatus: suggestion.clientStatus,
          recordDate: suggestion.recordDate,
          reportType: suggestion.reportType,
          isBot: suggestion.isBot,
          typeHeader:
            isNewGroup && suggestion.type
              ? (MENTION_TYPE_LABELS[suggestion.type] ?? suggestion.type)
              : undefined,
        }
      })
      searchCache.set(cacheKey(purpose, trimmedSearch), mapped)
      return [...broadcastSuggestions, ...mapped]
    },
    [listMentionSuggestions, purpose, broadcastFor],
  )
  loadSuggestions.getLocal = getLocalSuggestions

  // Slash-menu loader. Like `loadSuggestions` above (and `listEntitySuggestion`
  // elsewhere), this fetches from the server on every keystroke, passing the
  // typed text as `fullSearch` so the backend filters both prompts and
  // workflows. This replaces the old fetch-everything-once-then-filter-locally
  // approach, which hid saved prompts once the org had more than one page of
  // them or a workflow shared a similar name.
  const loadSlashSuggestions = useCallback(
    async (searchText: string): Promise<IMentionSuggestion[]> => {
      if (!listWorkflows || !listPrompts) return []
      const trimmed = searchText.trim()
      const params = {
        perPage: MAX_SLASH_SUGGESTIONS,
        ...(trimmed ? { filters: { fullSearch: trimmed } } : {}),
      }
      try {
        const [workflowsResponse, promptsResponse] = await Promise.all([
          listWorkflows(params),
          listPrompts(params),
        ])

        const matchedWorkflows = (workflowsResponse?.data?.data ?? []).map((w) => ({
          id: `workflow:${w.workflowName}`,
          name: `/${w.workflowName}`,
          subtitle: 'Workflow',
        }))

        const matchedPrompts = (promptsResponse?.data?.data ?? []).map((p) => ({
          id: `prompt:${p.id}`,
          name: `/${p.key}`,
          subtitle: 'Prompt',
        }))

        return [...matchedWorkflows, ...matchedPrompts].slice(0, MAX_SLASH_SUGGESTIONS)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load slash suggestions:', error)
        return []
      }
    },
    [listWorkflows, listPrompts],
  )

  return { loadSuggestions, loadSlashSuggestions }
}

export default useMentionSuggestions
